import { z } from "zod";
import { ComponentParameterSchema } from "./component-parameter";

// The API returns the pay-component identifier as `componentName`, but the
// wizard's internal domain uses `componentCode`. Bridge the two at the parse
// boundary so every downstream consumer keeps working with `componentCode`.
export const ImportValidationErrorSchema = z
  .object({
    row: z.number(),
    employeeCode: z.string(),
    componentName: z.string(),
    error: z.string(),
  })
  .transform(({ componentName, ...rest }) => ({
    ...rest,
    componentCode: componentName,
  }));
export type ImportValidationError = z.infer<typeof ImportValidationErrorSchema>;

export const UnmappedElementSchema = z
  .object({
    elementQualifiedName: z.string(),
    componentName: z.string(),
    parameters: z.array(ComponentParameterSchema),
  })
  .transform(({ componentName, ...rest }) => ({
    ...rest,
    componentCode: componentName,
  }));
export type UnmappedElement = z.infer<typeof UnmappedElementSchema>;

export const ImportStatusSchema = z.enum([
  "needs_mapping",
  "invalid",
  "valid",
  "committed",
]);
export type ImportStatus = z.infer<typeof ImportStatusSchema>;

export const ImportResultSchema = z.object({
  status: ImportStatusSchema,
  committed: z.number(),
  failed: z.number(),
  errors: z.array(ImportValidationErrorSchema),
  unmapped: z.array(UnmappedElementSchema),
  mappingsSaved: z.number(),
});
export type ImportResult = z.infer<typeof ImportResultSchema>;

/**
 * Domain-shaped import result for client-side persistence (#228).
 * Unlike {@link ImportResultSchema}, this matches the post-parse wizard state
 * (`componentCode` fields) rather than the API wire shape (`componentName`).
 */
export const StoredImportResultSchema = z.object({
  status: ImportStatusSchema,
  committed: z.number(),
  failed: z.number(),
  errors: z.array(
    z.object({
      row: z.number(),
      employeeCode: z.string(),
      componentCode: z.string(),
      error: z.string(),
    }),
  ),
  unmapped: z.array(
    z.object({
      elementQualifiedName: z.string(),
      componentCode: z.string(),
      parameters: z.array(ComponentParameterSchema),
    }),
  ),
  mappingsSaved: z.number(),
}) satisfies z.ZodType<ImportResult>;
